import { createContext, useContext } from 'react'
import type { Device, MoteLoopPayload, ToteStatusCoreData } from '@mote/api'
import { useMoteLoopMutation } from '@mote/api'
import { useMoteConversationContext } from '../../mote'
import { useMoteLandingPageOTLContext } from './MoteLandingPageOTLContext'

type CarouselData = Pick<Device, 'affirmativeCarousel' | 'assistiveCarousel'>

export interface MoteLoopContextValue {
  loop: (variables: { variables: MoteLoopPayload }) => void
  getToteStatus: () => ToteStatusCoreData
  getIsLoopy: () => boolean
  getCarousels: () => CarouselData
  isShown: boolean
  refreshKey: number
}

const MoteLoopContext = createContext<MoteLoopContextValue | null>(null)

export interface MoteLoopProviderProps {
  moteId: string
  children: JSX.Element
}

export function MoteLoopProvider(props: MoteLoopProviderProps) {
  const moteConversationContext = useMoteConversationContext()
  const moteLoopMutation = useMoteLoopMutation(props.moteId)
  const refreshKey = useMoteLandingPageOTLContext()

  return (
    <MoteLoopContext.Provider
      value={{
        loop: variables => moteLoopMutation.mutate(variables),
        getToteStatus,
        getIsLoopy,
        getCarousels,
        isShown: canBeShown(),
        refreshKey
      }}
    >
      {props.children}
    </MoteLoopContext.Provider>
  )

  function canBeShown() {
    return moteConversationContext.hasApiData && getIsLoopy()
  }

  function getCarousels(): CarouselData {
    return {
      affirmativeCarousel:
        moteConversationContext.getDevice().affirmativeCarousel,
      assistiveCarousel: moteConversationContext.getDevice().assistiveCarousel
    }
  }

  function getToteStatus(): ToteStatusCoreData {
    const moteStatus = moteConversationContext.getApiMoteStatus()

    return {
      createdByAvatar: moteStatus.createdByAvatar,
      createdByName: moteStatus.createdByName,
      wasCreatedBySameUser: moteStatus.wasCreatedBySameUser,
      studentName: moteStatus.studentName,
      lastSeenEpoch: moteStatus.lastSeenEpoch,
      lastLoopMode: moteStatus.lastLoopMode,
      lastTeply: moteStatus.lastTeply
    }
  }

  function getIsLoopy(): boolean {
    return moteConversationContext.getApiMoteStatus().isLoopy
  }
}

export function useUnsafeMoteLoopContext(): MoteLoopContextValue | null {
  return useContext(MoteLoopContext)
}

export function useMoteLoopContext(): MoteLoopContextValue {
  const value = useUnsafeMoteLoopContext()
  if (!value) {
    throw new Error('To useLoopContext you have to wrap it in MoteLoopProvider')
  }
  return value
}
